import { Control, Controller, RegisterOptions } from "react-hook-form";
import { useId, useMemo, useState } from "react";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../shadcn/popover";
import { Input } from "../shadcn/input";
import { Button } from "../shadcn/button";
import { Alert, AlertDescription, AlertTitle } from "../shadcn/alert";
import { AlertTriangle, Pencil, Plus } from "lucide-react";
import {
  isValidHexColor,
  adjustShadeBrightnessOnly as adjustShade,
  pickUnifiedTextColor,
  hexToRgb,
  getRelativeLuminance,
  contrastRatio,
} from "@/utils/generic";
import AppPreview from "../AppPreview";
import WidgetPreview from "../WidgetPreview";
import { type ColorTheme, DEFAULT_THEME } from "./colorTheme";

interface ColorItemProps {
  value: ColorTheme;
  isChecked: boolean;
  isBig?: boolean;
  isDisabled?: boolean;
  onChange: () => void;
}

type PreviewCommonProps = {
  logo?: string;
  icon?: string;
  customization?: Record<string, any>;
};

function renderPreviewByType(
  previewType: string,
  props: PreviewCommonProps
): React.ReactElement | null {
  switch ((previewType || "").toLowerCase()) {
    case "chat":
      return <AppPreview {...props} isSmall />;
    case "form":
      return <AppPreview {...props} isForm={true} isSmall />;
    case "widget":
      return <WidgetPreview {...props} isSmall />;
    // Add more mappings here as new preview components are introduced
    default:
      return <AppPreview {...props} isForm={false} isSmall />;
  }
}

const swatchClasses = (props: {
  isChecked: boolean;
  isBig?: boolean;
  isDisabled?: boolean;
}) =>
  cn(
    "flex cursor-pointer overflow-hidden border border-black/10 outline-offset-2 transition-all dark:border-white/15",
    props.isBig ? "h-8 w-8 rounded-[4px]" : "h-4 w-4 rounded-[4px]",
    props.isDisabled
      ? props.isChecked
        ? "opacity-50"
        : "opacity-20"
      : "opacity-100",
    props.isChecked
      ? "outline outline-[1.5px] outline-slate-900"
      : "outline-none hover:outline hover:outline-[1.5px] hover:outline-slate-300"
  );

const RadioItem = (props: ColorItemProps) => {
  return (
    <label className="py-1">
      <input
        type="radio"
        value={JSON.stringify(props.value)}
        checked={props.isChecked}
        disabled={props.isDisabled}
        onChange={props.onChange}
        className="sr-only"
      />
      <div
        className={swatchClasses(props)}
        tabIndex={props.isDisabled ? -1 : 0}
      >
        <div
          className="flex-1"
          style={{ backgroundColor: props.value.accent }}
        />
        <div
          className="flex-1"
          style={{ backgroundColor: props.value.primary }}
        />
      </div>
    </label>
  );
};

interface IOption {
  name: string;
  theme: ColorTheme;
}

const PRESET_THEMES: IOption[] = [
  { name: "Default", theme: DEFAULT_THEME },
  {
    name: "Yellow",
    theme: { accent: "#FED7AA", primary: "#92400E" },
  },
  {
    name: "Pink",
    theme: { accent: "#FECDD3", primary: "#9F1239" },
  },
  {
    name: "Purple",
    theme: { accent: "#D9D6FE", primary: "#4A1FB8" },
  },
  {
    name: "Blue",
    theme: { accent: "#BAE6FD", primary: "#075985" },
  },
  {
    name: "Green",
    theme: { accent: "#BEE3BA", primary: "#05603A" },
  },
  {
    name: "Gray",
    theme: { accent: "#D5D7DA", primary: "#36393E" },
  },
];

// What the custom popover opens on when nothing usable is selected yet: a
// new deployment stores the backend's `var(--primary)` defaults, which are
// not colors a user can edit.
const CUSTOM_SEED: ColorTheme = { accent: "#F0F0F0", primary: "#000000" };

type ColorFieldValue = ColorTheme & { isCustom?: boolean };

interface IColorSelectProps {
  label?: string;
  value?: ColorFieldValue;
  isBig?: boolean;
  preview?: string;
  isDisabled?: boolean;
  options: IOption[];
  hasCustomColor?: boolean;
  onChange: (value: ColorFieldValue) => void;
  name?: string;
}

const isValidHex = isValidHexColor;

const parseFieldValue = (
  v: ColorFieldValue | undefined
): ColorTheme & { isCustom: boolean } => {
  if (!v)
    return {
      primary: "",
      accent: "",
      isCustom: false,
    };
  return {
    primary: v.primary || "",
    accent: v.accent || "",
    isCustom: !!v.isCustom,
  };
};

const themesEqual = (a: ColorTheme, b: ColorTheme): boolean => {
  return a.primary === b.primary && a.accent === b.accent;
};

const isHexTheme = (theme: ColorTheme): boolean =>
  isValidHex(theme.primary) && isValidHex(theme.accent);

const seedFor = (current: ColorTheme): ColorTheme =>
  isHexTheme(current)
    ? { primary: current.primary, accent: current.accent }
    : CUSTOM_SEED;

const normalizeHex = (raw: string): string => {
  const trimmed = raw.trim();
  return trimmed === "" ? "" : `#${trimmed.replace(/^#+/, "")}`;
};

const luminance = (hex: string) => getRelativeLuminance(hexToRgb(hex));

interface HexInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

const HexInput = ({ label, value, onChange }: HexInputProps) => {
  const id = useId();
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(normalizeHex(e.target.value))}
          placeholder="#000000"
          className="font-mono text-sm"
        />
        <input
          type="color"
          aria-label={`${label} picker`}
          value={isValidHex(value) ? value : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            "h-8 w-10 shrink-0 cursor-pointer rounded-md border-[1px] border-input bg-transparent p-1 shadow-sm",
            "focus-visible:outline-none focus-visible:ring-[1px] focus-visible:ring-ring",
            "[&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-[0.1875rem] [&::-webkit-color-swatch]:border-0 [&::-moz-color-swatch]:rounded-[0.1875rem] [&::-moz-color-swatch]:border-0"
          )}
        />
      </div>
    </div>
  );
};

const ContrastWarning = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <Alert variant="warning" className="py-2">
    <AlertTriangle className="h-4 w-4" />
    <AlertTitle>{title}</AlertTitle>
    <AlertDescription>{description}</AlertDescription>
  </Alert>
);

export const ColorSelect = (props: IColorSelectProps) => {
  const current = useMemo(() => parseFieldValue(props.value), [props.value]);
  const [open, setOpen] = useState(false);
  const [tempTheme, setTempTheme] = useState<ColorTheme>(() =>
    seedFor(current)
  );

  const isCustomSelected = !!current.isCustom;
  const canApply = isHexTheme(tempTheme);

  // Picking a preset replaces the field value, so the custom pair would be
  // gone from the trigger and the popover would reopen on the preset. Keep
  // the last custom pair until the user applies a different one.
  const [rememberedCustom, setRememberedCustom] = useState<ColorTheme | null>(
    null
  );
  const selectedCustom =
    isCustomSelected && isHexTheme(current)
      ? { primary: current.primary, accent: current.accent }
      : null;
  if (
    selectedCustom &&
    (!rememberedCustom || !themesEqual(selectedCustom, rememberedCustom))
  ) {
    setRememberedCustom(selectedCustom);
  }
  const customTheme = selectedCustom ?? rememberedCustom;

  const handleOpenChange = (next: boolean) => {
    if (next && props.isDisabled) return;
    if (next) setTempTheme(customTheme ?? seedFor(current));
    setOpen(next);
  };

  const applyCustom = () => {
    if (!canApply) return;
    props.onChange({ ...tempTheme, isCustom: true });
    setOpen(false);
  };

  const backgroundTooDark =
    isValidHex(tempTheme.accent) &&
    contrastRatio(luminance(tempTheme.accent), 0) < 4.5;
  const buttonTooLight =
    isValidHex(tempTheme.primary) &&
    contrastRatio(luminance(tempTheme.primary), 1) < 3;

  return (
    <div>
      {props.label && <NoxusFormLabel label={props.label} name={props.name} />}
      <div
        className={cn(
          "flex items-center flex-wrap",
          props.isBig ? "gap-4 p-1" : "gap-2"
        )}
      >
        {props.options.map((option, index) => (
          <RadioItem
            key={`color-option${index}`}
            value={option.theme}
            isChecked={themesEqual(current, option.theme) && !current.isCustom}
            isBig={props.isBig}
            isDisabled={props.isDisabled}
            onChange={() => props.onChange(option.theme)}
          />
        ))}

        {props.hasCustomColor && (
          <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  swatchClasses({
                    isChecked: isCustomSelected,
                    isBig: props.isBig,
                    isDisabled: props.isDisabled,
                  }),
                  "items-center justify-center",
                  !customTheme && "border-dashed border-foreground"
                )}
                aria-label={
                  customTheme ? "Edit custom color" : "Add custom color"
                }
              >
                {customTheme ? (
                  <>
                    <div
                      className="flex-1 h-full flex items-center justify-center"
                      style={{ backgroundColor: customTheme.accent }}
                    />
                    <div
                      className="flex-1 h-full flex items-center justify-center relative"
                      style={{ backgroundColor: customTheme.primary }}
                    >
                      <Pencil
                        className={
                          props.isBig ? "size-3 absolute" : "size-2 absolute"
                        }
                        style={{
                          color: pickUnifiedTextColor(
                            customTheme.primary,
                            adjustShade(customTheme.primary)
                          ),
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <Plus className={props.isBig ? "size-4" : "size-3"} />
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" side="top" className="w-80">
              <div className="space-y-3">
                <div>
                  {renderPreviewByType(props.preview || "widget", {
                    customization: {
                      main_color: {
                        primary: tempTheme.primary,
                        accent: tempTheme.accent,
                      },
                      apply_custom_theme: true,
                    },
                  })}
                </div>
                <div className="text-sm font-medium">Custom colors</div>

                <HexInput
                  label="Primary (Backgrounds)"
                  value={tempTheme.accent}
                  onChange={(accent) => setTempTheme((t) => ({ ...t, accent }))}
                />
                {backgroundTooDark && (
                  <ContrastWarning
                    title="Background color is too dark"
                    description="Text may be hard to read on it."
                  />
                )}

                <HexInput
                  label="Secondary (Buttons)"
                  value={tempTheme.primary}
                  onChange={(primary) =>
                    setTempTheme((t) => ({ ...t, primary }))
                  }
                />
                {buttonTooLight && (
                  <ContrastWarning
                    title="Button color is too light"
                    description="The white icon may be hard to see on it."
                  />
                )}

                <Button
                  className="w-full mt-2"
                  disabled={!canApply}
                  onClick={applyCustom}
                >
                  Apply colors
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
};

interface IColorSelectFieldProps {
  control: Control<any>;
  name: string;
  options?: IOption[];
  hasCustomColor?: boolean;
  defaultValue?: ColorFieldValue;
  isBig?: boolean;
  isDisabled?: boolean;
  label?: string;
  preview?: string;
  rules?: RegisterOptions;
}

export const ColorThemeSelectField = (props: IColorSelectFieldProps) => {
  const options = props.options || PRESET_THEMES;

  const defaultValue = useMemo(() => {
    if (!props.defaultValue) return options[0]?.theme;
    return props.defaultValue;
  }, [props.defaultValue, options]);

  return (
    <Controller
      control={props.control}
      name={props.name}
      defaultValue={defaultValue}
      rules={props.rules}
      render={({ field: { onChange, value } }) => (
        <ColorSelect
          value={value}
          label={props.label}
          isBig={props.isBig}
          preview={props.preview}
          isDisabled={props.isDisabled}
          options={options}
          hasCustomColor={props.hasCustomColor}
          onChange={onChange}
          name={props.name}
        />
      )}
    />
  );
};

export { PRESET_THEMES, type ColorTheme };
